import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Label } from '@/components/shadcn/label'
import { Input } from '@/components/shadcn/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import {
  useGetApiCompaniesCurrentActionmodulesFiltered,
  useGetApiCompaniesCurrentServicemodulesFiltered,
} from '@/api/generated/company-management-user/company-management-user'
import { InAppNotificationActionType } from '@/api/model/inAppNotificationActionType'
import type { InAppNotificationActionRequest } from '@/api/model/inAppNotificationActionRequest'

// ShopModule is deliberately missing: the new portal has no visibility-filtered
// ShopModule endpoint (yet). Once it exists, add it here + in the reference switch.
function getActionTypeLabels(t: TFunction): Record<InAppNotificationActionType, string> {
  return {
    ActionModule: t('actionType.ActionModule'),
    ServiceModule: t('actionType.ServiceModule'),
    Profile: t('actionType.Profile'),
    Wallet: t('actionType.Wallet'),
    ExternalUrl: t('actionType.ExternalUrl'),
    ShopModule: t('actionType.ShopModule'),
  }
}

const SELECTABLE_TYPES: InAppNotificationActionType[] = [
  InAppNotificationActionType.ActionModule,
  InAppNotificationActionType.ServiceModule,
  InAppNotificationActionType.Profile,
  InAppNotificationActionType.Wallet,
  InAppNotificationActionType.ExternalUrl,
]

interface Props {
  action: InAppNotificationActionRequest
  onChange: (action: InAppNotificationActionRequest) => void
  isCompanyWideAssigned: boolean
  groupIds: number[]
  employeeUserIds: string[]
  showError?: boolean
}

export function NotificationActionFields({
  action, onChange, isCompanyWideAssigned, groupIds, employeeUserIds, showError,
}: Props) {
  const { t } = useTranslation('notifications')
  const actionTypeLabels = getActionTypeLabels(t)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="action-text">{t('dialog.linking.actionText')}</Label>
        <Input
          id="action-text"
          value={action.actionText ?? ''}
          onChange={(e) => onChange({ ...action, actionText: e.target.value })}
          placeholder={t('dialog.linking.actionTextPlaceholder')}
        />
      </div>

      <div className="flex flex-row gap-1.5">
        <div className="flex flex-col gap-1.5">
          <Label>{t('dialog.linking.type')}</Label>
          <Select
            value={action.type}
            onValueChange={(value) =>
              onChange({ type: value as InAppNotificationActionType, actionText: action.actionText, reference: undefined })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SELECTABLE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{actionTypeLabels[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ActionReferenceField
          action={action}
          onChange={onChange}
          isCompanyWideAssigned={isCompanyWideAssigned}
          groupIds={groupIds}
          employeeUserIds={employeeUserIds}
        />
      </div>

      {showError && !isActionValid(action) && (
        <p className="text-xs text-destructive">
          {referenceRequired(action.type)
            ? t('dialog.linking.errors.linkTextAndTarget')
            : t('dialog.linking.errors.linkTextRequired')}
        </p>
      )}
    </div>
  )
}

function ActionReferenceField({
  action, onChange, isCompanyWideAssigned, groupIds, employeeUserIds,
}: Omit<Props, 'showError'>) {
  const { t } = useTranslation('notifications')
  const visibilityParams = {
    isCompanyWideAssigned,
    groupIds,
    employeeUserIds,
  }

  const { data: actionModulesData, isLoading: actionLoading } =
    useGetApiCompaniesCurrentActionmodulesFiltered(visibilityParams, {
      query: { enabled: action.type === 'ActionModule' },
    })
  const { data: serviceModulesData, isLoading: serviceLoading } =
    useGetApiCompaniesCurrentServicemodulesFiltered(visibilityParams, {
      query: { enabled: action.type === 'ServiceModule' },
    })

  const actionModules = (actionModulesData?.data ?? []) as { id?: number; name?: string | null }[]
  const serviceModules = (serviceModulesData?.data ?? []) as { id?: number; name?: string | null }[]

  const modules = action.type === 'ActionModule' ? actionModules
    : action.type === 'ServiceModule' ? serviceModules
    : []
  const isModuleType = action.type === 'ActionModule' || action.type === 'ServiceModule'
  const isLoading = action.type === 'ActionModule' ? actionLoading : serviceLoading

  // Discard the reference when the selected module no longer exists under the current visibility.
  useEffect(() => {
    if (!isModuleType || isLoading) return
    if (action.reference && !modules.some((m) => String(m.id) === action.reference)) {
      onChange({ ...action, reference: undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModuleType, isLoading, action.reference, modules.length])

  if (action.type === 'Profile' || action.type === 'Wallet') {
    // Display no action needed
    return (
      <div className="flex flex-col gap-1.5">
        <Label>{t('dialog.linking.target')}</Label>
        <Input value={t('dialog.linking.noTargetNeeded')} disabled readOnly />
      </div>
    )
  }

  if (action.type === 'ExternalUrl') {
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="action-url">{t('dialog.linking.target')}</Label>
        <Input
          id="action-url"
          value={action.reference ?? ''}
          onChange={(e) => onChange({ ...action, reference: e.target.value })}
          placeholder="https://..."
        />
      </div>
    )
  }

  const label = getActionTypeLabels(t)[action.type]

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{t('dialog.linking.target')}</Label>
      <Select
        value={action.reference ?? ''}
        onValueChange={(value) => onChange({ ...action, reference: value })}
        disabled={isLoading}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={isLoading ? t('dialog.linking.loading') : t('dialog.linking.selectTarget', { type: label })}
          />
        </SelectTrigger>
        <SelectContent>
          {modules
            .filter((m) => m.id != null)
            .map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {m.name ?? `#${m.id}`}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function referenceRequired(type: InAppNotificationActionType): boolean {
  return type === 'ActionModule' || type === 'ServiceModule' || type === 'ExternalUrl' || type === 'ShopModule'
}

export function isActionValid(action: InAppNotificationActionRequest | undefined): boolean {
  if (!action) return true
  if (!action.actionText) return false
  return referenceRequired(action.type) ? !!action.reference : true
}
